// ============================================
// Auth Store (Zustand)
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Admin } from '../types';
import { authApi } from '../utils/api';

interface AuthState {
  admin: Admin | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  fetchMe: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      admin: null,
      token: null,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login(email, password);
          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          set({ token: access_token });
          await get().fetchMe();
        } catch (error: any) {
          const detail = error.response?.data?.detail;
          const message = typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((item) => item?.msg).filter(Boolean).join('. ') || 'Invalid login request'
              : 'Login failed';
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      logout: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('admin');
        set({ admin: null, token: null });
      },

      fetchMe: async () => {
        try {
          const response = await authApi.me();
          set({ admin: response.data });
        } catch {
          get().logout();
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        admin: state.admin,
        token: state.token,
      }),
    }
  )
);

// Initialize auth on app load
if (typeof window !== 'undefined') {
  const token = localStorage.getItem('access_token');
  if (token) {
    useAuthStore.getState().fetchMe();
  }
}